import { Dialog } from "primereact/dialog";
import { Messages } from "primereact/messages";
import { ReactNode, useEffect, useRef, useState } from "react";
import { PrimeIcons } from "primereact/api";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Dropdown } from "primereact/dropdown";
import { FormFieldTypes } from "../app_utils/constants/FormFieldTypes";
import { getFormFieldComponent } from "../app_utils/components/FormFieldTemplates";
import { formatString } from "../app_utils/utils/Utils";
import { MISSING_FORM_INPUT_MESSAGE } from "../app_utils/constants/ErrorMessages";
import { CSS_COL_12, CSS_COL_6, RECORD_STATUSES } from "../app_utils/constants/Constants";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import { MessageUtils } from "../app_utils/utils/MessageUtils";
import * as labels from "../app_utils/constants/Labels";

interface ModalType {
  children?: ReactNode;
  messageRef?: any;
  record: any;
  reloadFn: any;
  isOpen: boolean;
  toggle: () => void;
}

interface RecordType {
  id?: number;
  name: string;
  description: string;
  provider: string;
  productType: string;
  apiKey: string;
  appId: string;
  merchantId: string;
  callbackSecret: string;
  baseUrl: string;
  priority: number;
  status: string;
}

const PAYMENT_PROVIDERS = [
  { label: "KaCyber Pay", value: "KACYBER" },
  { label: "Flutterwave", value: "FLUTTERWAVE" },
  { label: "Pesapal", value: "PESAPAL" },
  { label: "Stripe", value: "STRIPE" },
];

const PAYMENT_PRODUCT_TYPES = [
  { label: "SMS Wallet", value: "SMS_WALLET" },
  { label: "Data Bundles", value: "DATA_BUNDLE" },
  { label: "Bank Transfers", value: "BANK_TRANSFER" },
  { label: "Mobile Money Disbursements", value: "MOBILE_MONEY_DISBURSEMENT" },
];

const PaymentGatewayFormDialog = (props: ModalType) => {
  const [record, setRecord] = useState<RecordType | null>(props?.record || null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const message = useRef<any>();

  useEffect(() => {
    setRecord(
      props?.record
        ? {
            id: props.record.id,
            name: props.record.name || "",
            description: props.record.description || "",
            provider: props.record.provider || "KACYBER",
            productType: props.record.productType || "",
            apiKey: props.record.apiKey || "",
            appId: props.record.appId || "",
            merchantId: props.record.merchantId || "",
            callbackSecret: props.record.callbackSecret || "",
            baseUrl: props.record.baseUrl || "",
            priority: props.record.priority || 1,
            status: props.record.status || "Deactivated",
          }
        : null
    );
  }, [props?.record]);

  const clearForm = () => {
    setRecord(null);
  };

  const validateForm = () => {
    if (!record?.name || !record?.provider || !record?.apiKey) {
      MessageUtils.showErrorMessage(message, "Please fill in all required fields");
      return false;
    }
    return true;
  };

  const saveUser = () => {
    if (!validateForm()) return;
    setIsSaving(true);
    const url = props?.record
      ? "/api/v1/payment-gateways/" + props.record.id
      : "/api/v1/payment-gateways";
    const method = props?.record ? "putRequestWithJsonResponse" : "postRequestWithJsonResponse";

    new BaseApiServiceImpl(url)
      [method](record)
      .then(async (response) => {
        setIsSaving(false);
        MessageUtils.showSuccessMessage(
          props?.messageRef,
          labels.LABEL_RECORD_SAVED_SUCCESSFULLY
        );
        closeDialog();
        props?.reloadFn();
      })
      .catch((error) => {
        setIsSaving(false);
        MessageUtils.showErrorMessage(message, error.message);
      });
  };

  const closeDialog = () => {
    clearForm();
    props.toggle();
  };

  const userDetailsDialogFooter = (
    <>
      <Button
        label={labels.LABEL_CANCEL}
        icon={PrimeIcons.TIMES}
        className="p-button-text"
        onClick={closeDialog}
      />
      <Button
        label={labels.LABEL_SAVE}
        icon={PrimeIcons.SAVE}
        className="p-button-secondary"
        onClick={saveUser}
        loading={isSaving}
      />
    </>
  );

  const isEdit = !!props?.record;

  return (
    <Dialog
      visible={props.isOpen}
      header={isEdit ? "Edit Payment Gateway" : "New Payment Gateway"}
      footer={userDetailsDialogFooter}
      modal
      className="p-fluid"
      onHide={closeDialog}
      style={{ width: "50vw" }}
    >
      <div className="grid">
        <div className="col-12">
          <Messages ref={message} style={{ width: "100%" }} />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="name">Name</label>
          <InputText
            id="name"
            value={record?.name || ""}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, name: e.target.value }))}
            className="w-full mt-1"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="provider">Provider</label>
          <Dropdown
            id="provider"
            value={record?.provider || "KACYBER"}
            options={PAYMENT_PROVIDERS}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, provider: e.value }))}
            className="w-full mt-1"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="productType">Product Type</label>
          <Dropdown
            id="productType"
            value={record?.productType || ""}
            options={PAYMENT_PRODUCT_TYPES}
            optionValue="value"
            optionLabel="label"
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, productType: e.value }))}
            className="w-full mt-1"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="apiKey">API Key</label>
          <Password
            id="apiKey"
            value={record?.apiKey || ""}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, apiKey: e.target.value }))}
            className="w-full mt-1"
            feedback={false}
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="appId">App ID</label>
          <InputText
            id="appId"
            value={record?.appId || ""}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, appId: e.target.value }))}
            className="w-full mt-1"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="merchantId">Merchant ID</label>
          <InputText
            id="merchantId"
            value={record?.merchantId || ""}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, merchantId: e.target.value }))}
            className="w-full mt-1"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="callbackSecret">Callback Secret</label>
          <Password
            id="callbackSecret"
            value={record?.callbackSecret || ""}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, callbackSecret: e.target.value }))}
            className="w-full mt-1"
            feedback={false}
          />
        </div>
        <div className="col-12">
          <label htmlFor="baseUrl">Base URL</label>
          <InputText
            id="baseUrl"
            value={record?.baseUrl || ""}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, baseUrl: e.target.value }))}
            className="w-full mt-1"
            placeholder="https://api.payment-provider.com"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="priority">Priority</label>
          <InputText
            id="priority"
            type="number"
            value={record?.priority || 1}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, priority: parseInt(e.target.value) || 1 }))}
            className="w-full mt-1"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="status">Status</label>
          <Dropdown
            id="status"
            value={record?.status || "Deactivated"}
            options={RECORD_STATUSES}
            optionValue="id"
            optionLabel="name"
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, status: e.value }))}
            className="w-full mt-1"
          />
        </div>
      </div>
    </Dialog>
  );
};

export default PaymentGatewayFormDialog;
